/** @jsxImportSource @emotion/react */
import { faFilePdf } from "@fortawesome/pro-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useClientPortal } from "../../../context/ClientPortal";
import { useFields, usePagination } from "../../../hooks";
import { callAvosApi } from "../../../utils/useAvosApiHook";
import { AttachmentButton } from "../../Helper/AttachmentLink";
import { getRowValues, getTableHead, Table } from "../../Helper/Table";
import { formatDateTime } from "../Layers/fields/formatters";

const ClientReports = () => {
    const { selectedTenant } = useClientPortal();
    const { applyDisplayFilterLayerFields } = useFields();

    const getReports = async (tenantId, clientId, offset, limit) => {
        const { data } = await callAvosApi(`/client-portal/${tenantId}/${clientId}/report?limit=${limit}&offset=${offset}`,);
        return data;
    };
    const { offset, limit } = usePagination();

    const { data, isLoading } = useQuery({
        queryKey: ["reports", selectedTenant?.id, selectedTenant?.client_id, offset, limit],
        queryFn: () => getReports(selectedTenant?.id, selectedTenant?.client_id, offset, limit),
        enabled: !!selectedTenant?.id });

    const portalFields = useMemo(() => applyDisplayFilterLayerFields(selectedTenant?.config?.client_portal_overview_fields).map((field) => ({
        ...field,
        display_component: (item) => field.set_value(item).display_component()
    })), [selectedTenant?.config?.client_portal_overview_fields]);

    const commonFields = [
        {
            label: "Date",
            name: "created",
            lg: 2,
            display_component: (report) => formatDateTime(dayjs(report.created), "DD/MM/YYYY - HH:mm"),
        },
        {
            label: "PDF",
            name: "url",
            display_component: (report) => <AttachmentButton url={report.url} icon={faFilePdf} />,
            lg: 1,
        },
    ];

    const tableColumns = [...portalFields, ...commonFields];

    const header = useMemo(() => (
        <tr>
            {getTableHead(tableColumns)}
        </tr>
    ), [tableColumns]);


    return (
        <Table
            header={header}
            isLoading={isLoading}
            withWrapper
            count={data?.count}
        >
            <tbody>
                {data?.results.map((result, index) => (
                    <tr key={index} title={result.id}>
                        {getRowValues(result, tableColumns)}
                    </tr>
                ))}
            </tbody>
        </Table>
    );
};

export default ClientReports;
